// Single source of truth for all SEO/head metadata — used by the React app
// (src/components/Seo.jsx) AND the build-time prerender script
// (scripts/prerender-meta.mjs), so static HTML and client-rendered head can
// never drift apart. Plain JS only — no JSX, no React imports.
// Canonical/OG URLs always use the production domain even on preview, so
// metadata is correct once the domain repoints.

export const SITE_URL = "https://nischint.care";
export const SITE_NAME = "NISCHINT";
export const SHARE_IMAGE = `${SITE_URL}/og-share-card.jpg`;
export const OG_IMAGE_ALT = "NISCHINT — Family safety alerts for your loved ones";
export const LOGO_URL = `${SITE_URL}/logo.png`;

export const SOCIALS = [
  "https://www.facebook.com/profile.php?id=61591583519049",
  "https://www.instagram.com/nischintcare/",
  "https://www.linkedin.com/in/nischintcare/",
  "https://x.com/nischintcare",
  "https://www.threads.com/@nischintcare",
  "https://www.youtube.com/@NischintCare",
];

export const ORGANIZATION = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "NISCHINT",
  legalName: "Skybyte Venture Private Limited",
  url: SITE_URL,
  logo: LOGO_URL,
  description:
    "Family safety platform for women, children, and seniors across Indian cities, launching first in Mumbai, Thane, Bengaluru, Delhi and Jaipur.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Mumbai",
    addressRegion: "Maharashtra",
    addressCountry: "IN",
  },
  founder: [
    { "@type": "Person", name: "Feroz Mohammad Shaikh", jobTitle: "Founder & CEO" },
    { "@type": "Person", name: "Pravin Arora", jobTitle: "Co-Director & CFO" },
    { "@type": "Person", name: "Lokesh Thada", jobTitle: "Co-Director & COO" },
  ],
  sameAs: SOCIALS,
};

// BlogPosting schema for a single post. `path` is the route path (/blog/<slug>).
export const makeBlogPostingSchema = (post, path) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.description,
  datePublished: post.publishedDate,
  author: { "@type": "Organization", name: post.author || "NISCHINT", url: SITE_URL },
  publisher: {
    "@type": "Organization",
    name: "NISCHINT",
    logo: { "@type": "ImageObject", url: LOGO_URL },
  },
  image: post.featuredImage ? `${SITE_URL}${post.featuredImage}` : SHARE_IMAGE,
  mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${path}` },
});

export const SAFETY_TRUST_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How are volunteers verified?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Nischint Saathi volunteers are run by the Nischint Seva Foundation. Every Saathi's identity is checked; anyone in a one-on-one role, such as elder companion visits, must also clear police verification. Volunteer response through the NISCHINT app is not live today.",
      },
    },
    {
      "@type": "Question",
      name: "What does a verified badge on NISCHINT mean?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A verified badge reflects completion of the applicable NISCHINT checks. It is not a guarantee of future conduct.",
      },
    },
    {
      "@type": "Question",
      name: "How do I report a platform concern or grievance to NISCHINT?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "For a platform-related concern (not an emergency), contact NISCHINT support at support@nischint.care or the grievance channel at privacy@nischint.care. This is not an emergency dispatch channel — in an emergency, contact local emergency services directly (police 100, ambulance 108).",
      },
    },
  ],
};

const PLANS_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Is there a free trial?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes — every plan starts with a 7-day free trial, then billing is monthly. Prices include GST.",
      },
    },
    {
      "@type": "Question",
      name: "What does the wearable add-on cost?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The wearable is a planned add-on.",
      },
    },
  ],
};

// /kids page — FAQ items are the single source for BOTH the visible accordion
// (KidsPage.jsx) and the FAQPage JSON-LD below (same pattern as
// SAFETY_TRUST_FAQ / PLANS_FAQ). Copy verbatim from KIDS_PAGE_COPY.md.
export const KIDS_FAQ_ITEMS = [
  { q: "Does my child need a phone?", a: "No. Younger children can carry the Child Tracker and the ID tag instead. A phone is optional and adds a few extras for older children." },
  { q: "Will my child's school allow the tracker?", a: "It has no camera, microphone, calling or screen, which are the things schools usually object to. We'll give you a short note to share with the school." },
  { q: "Can you hear or see my child?", a: "No. NISCHINT has no microphone or camera access in Kids mode, and never will." },
  { q: "Who can see my child's location?", a: "Only the guardians you add. A pickup person never sees your child's location; they only see the pickup place and time window." },
  { q: "Does the pickup person need the NISCHINT app?", a: "No. They verify their phone number once, then tap your child's ID card with their phone." },
  { q: "What if the tracker's battery dies?", a: "You still see the last known place and time. You also get a heads-up when the battery is low." },
  { q: "How is my child's data protected?", a: "We ask for verified parental consent before any child feature, collect only what safety needs, and follow India's Digital Personal Data Protection Act." },
  { q: "When can I start?", a: "Kids mode is launching soon. Join the waitlist and we'll tell you the day it opens." },
];

const KIDS_TITLE = "NISCHINT for Kids: School-Day Safety, Not Surveillance";
const KIDS_DESCRIPTION =
  "Know your child reached school, who picked them up, and when a plan breaks. No phone needed, no listening, no screen spying.";

const KIDS_WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: KIDS_TITLE,
  url: `${SITE_URL}/kids`,
  description: KIDS_DESCRIPTION,
  isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
};

const KIDS_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: KIDS_FAQ_ITEMS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const ROUTES = {
  "/": {
    title: "NISCHINT — Family safety alerts for your loved ones",
    description:
      "NISCHINT alerts your family circle when it matters: live location sharing, I'm-safe check-ins, and an SOS with one-tap access to call 112. Launching first in Mumbai, Thane, Bengaluru, Delhi and Jaipur.",
  },
  "/about": {
    title: "Why NISCHINT exists — About us",
    description:
      "The story and mission behind NISCHINT: an AI-powered urban safety platform built for Indian families, launching first in Mumbai, Thane, Bengaluru, Delhi and Jaipur.",
  },
  "/press": {
    title: "Press & Media — NISCHINT",
    description:
      "Company background, press resources, and media contact for NISCHINT, the AI-powered urban safety platform from Skybyte Venture Pvt. Ltd.",
  },
  "/thane": {
    title: "NISCHINT in Thane — Urban safety, launching locally",
    description:
      "NISCHINT launches first in Thane: AI-powered personal safety for women, children, and seniors.",
  },
  "/blog": {
    title: "Blog — NISCHINT",
    description:
      "Practical safety guides for Indian families: medical emergencies, elder care, community safety, and the NISCHINT pilot in Thane.",
  },
  "/careers": {
    title: "Careers at NISCHINT",
    description:
      "Work with the team building AI-powered urban safety for women, children, and seniors across Indian cities.",
  },
  "/trust-and-safety": {
    title: "Trust & Safety — NISCHINT",
    description:
      "How NISCHINT approaches trust and safety: verification, privacy controls, incident reporting, and community guidelines.",
    jsonld: [SAFETY_TRUST_FAQ],
  },
  "/law-enforcement-guidelines": {
    title: "Law Enforcement Guidelines — NISCHINT",
    description:
      "Guidelines for law enforcement agencies requesting user data or cooperation from NISCHINT.",
  },
  "/security": {
    title: "Security & Responsible Disclosure — NISCHINT",
    description:
      "NISCHINT's security practices and how to report a vulnerability through responsible disclosure.",
  },
  "/plans": {
    title: "Plans & Pricing — NISCHINT",
    description:
      "Simple plans for every family: start with a 7-day free trial, then Individual ₹299/month or Family ₹999/month. Prices include GST.",
    jsonld: [PLANS_FAQ],
  },
  "/kids": {
    title: KIDS_TITLE,
    description: KIDS_DESCRIPTION,
    image: "/images/kids/og-kids.jpg",
    imageAlt: "NISCHINT for Kids — school-day safety, not surveillance",
    jsonld: [KIDS_WEBPAGE, KIDS_FAQ],
  },
  "/for-nri-families": {
    title: "NISCHINT for NRI Families",
    description:
      "You're 12,000 km away. NISCHINT watches over your family in India and alerts you only when it matters.",
    noindex: true,
  },
  "/investors": {
    title: "Investors — NISCHINT",
    description:
      "A platform opportunity with real-world impact: NISCHINT's investor overview from Skybyte Venture Pvt. Ltd.",
  },
  "/partners": {
    title: "Partners — NISCHINT",
    description:
      "Partner with NISCHINT: pathways for housing societies, workplaces, institutions, and government bodies.",
  },
  // Consolidated into /trust-and-safety. Kept as a minimal route whose raw
  // HTML carries rel=canonical → /trust-and-safety (NOT noindex — canonical +
  // noindex sends conflicting signals). Excluded from the sitemap; the real
  // 301 lives at the user's Cloudflare edge.
  "/safety-trust": {
    title: "Safety & Trust, in the Open — NISCHINT",
    description:
      "How NISCHINT verifies the humans in the loop, and the processes that keep the platform accountable.",
    canonical: `${SITE_URL}/trust-and-safety`,
    sitemap: false,
  },
  "/privacy": {
    title: "Privacy Policy — NISCHINT",
    description:
      "How NISCHINT collects, uses, and protects your personal data, and how to reach our grievance officer.",
  },
  "/terms": {
    title: "Terms of Service — NISCHINT",
    description:
      "The terms that govern your use of NISCHINT, including subscriptions and billing.",
  },
  "/dpdp-consent": {
    title: "DPDP Consent Notice — NISCHINT",
    description:
      "NISCHINT's consent notice under India's Digital Personal Data Protection Act.",
  },
  "/delete-account": {
    title: "Delete your NISCHINT account",
    description:
      "How to delete your NISCHINT account — in the app or by email — and what data is removed.",
  },
  "/admin": {
    title: "Admin — NISCHINT",
    description: "NISCHINT admin dashboard.",
    noindex: true,
  },
};

// Resolve metadata for any pathname, including dynamic blog posts.
// `post` (parsed frontmatter) is required for /blog/<slug> paths.
export const resolveMeta = (pathname, post) => {
  if (pathname.startsWith("/blog/") && post) {
    return {
      title: `${post.title} — NISCHINT`,
      description: post.description,
      noindex: post.noindex === true,
      type: "article",
      image: post.featuredImage,
      imageAlt: post.featuredImage ? post.title : undefined,
      jsonld: [makeBlogPostingSchema(post, pathname)],
    };
  }
  const meta = ROUTES[pathname] || {
    title: ROUTES["/"].title,
    description: ROUTES["/"].description,
  };
  return meta;
};

// Canonical absolute URL for a route path.
export const canonicalFor = (path) => `${SITE_URL}${path === "/" ? "/" : path}`;

// Absolute og:image URL from an optional per-page image path.
export const imageFor = (image) =>
  image ? (image.startsWith("http") ? image : `${SITE_URL}${image}`) : SHARE_IMAGE;
